/* oxlint-disable typescript/no-non-null-assertion */
import { IConfig } from '@cian/config/shared';
import { Reducer } from 'redux';

import { addFailedAction } from './failedAction';
import { TThunkAction } from './index';
import { ESubscriptionsStatus, ISubscriptionsStatusChanged } from './subscriptions';
import { ECategoryType } from '../repositories/subscriptions/entities/entities/CategorySchema';
import { fetchEditSubscription } from '../repositories/subscriptions/v1/edit-subscription';
import { trackingPageType, trackSubscriptionDelete, trackSubscriptionToggle } from '../tracking';
import { getCommonCategoryIdToLabel, serializeDeviceType, serializeLabel } from '../utils/tracking';

export interface IIsActiveCategoryParams {
  categoryName: string;
  categoryId?: number;
  isActive: boolean;
}

export interface IIsActiveCategoryUpdated extends IIsActiveCategoryParams {
  type: 'IIsActiveCategoryUpdated';
}

export interface IIsActiveCategoryFailed extends IIsActiveCategoryParams {
  type: 'IIsActiveCategoryFailed';
}

export function updateIsActiveCategory(params: IIsActiveCategoryParams): TThunkAction {
  return (dispatch, getState, context) => {
    const { token } = getState();
    const apiParams = token
      ? Object.assign({}, params, { token, isActive: !params.isActive })
      : Object.assign({}, params, { isActive: !params.isActive });

    /** Обновляем UI сразу */
    dispatch<IIsActiveCategoryUpdated>({
      type: 'IIsActiveCategoryUpdated',
      ...params,
      isActive: !params.isActive,
    });

    /** Если апи зафейлилось - откатываем состояние UI до предыдущего */
    return fetchEditSubscription({ httpApi: context.httpApi, parameters: apiParams }).catch(() => {
      dispatch<ISubscriptionsStatusChanged>({
        status: ESubscriptionsStatus.Failed,
        type: 'ISubscriptionsStatusChanged',
      });

      dispatch(
        addFailedAction({
          type: 'IIsActiveCategoryUpdated',
          ...params,
        }),
      );

      dispatch<IIsActiveCategoryFailed>({
        type: 'IIsActiveCategoryFailed',
        ...params,
      });
    });
  };
}

export function onCategoryToggle(params: IIsActiveCategoryParams): TThunkAction {
  return (dispatch, getState, context) => {
    const { config } = context;
    const { categoryName, categoryId, isActive } = params;

    const page = {
      pageType: trackingPageType,
      siteType: serializeDeviceType(getState().deviceType),
    };

    const options = {
      label: serializeLabel({ categoryName, categoryId }, config),
      page,
    };

    if (categoryName === 'blogs' && isActive) {
      trackSubscriptionDelete(options);
    }

    /** в случае общих подписок categoryId всегда есть */
    if (checkIsCategoryTrackable(categoryName) && categoryId && isNeedTrackCommonCategory(categoryId, config)) {
      trackSubscriptionToggle({
        label: serializeLabel({ categoryName, categoryId }, config),
        isActive,
        page,
      });
    }

    dispatch(updateIsActiveCategory(params));
  };
}

function checkIsCategoryTrackable(categoryName: string) {
  return ['common_subscriptions', 'recommendations_favourites', 'recommendations_search'].includes(categoryName);
}

function isNeedTrackCommonCategory(categoryId: number, config: IConfig): boolean {
  const mapping = getCommonCategoryIdToLabel(config);

  return !!mapping[categoryId];
}

export type TCategoriesAction = IIsActiveCategoryUpdated | IIsActiveCategoryFailed;

export interface IGroup {
  title: string;
  categoryGroup: string;
  categories: ICategory[];
}

export interface ICategory {
  title: string; // В случае некоторых подписок тут должна быть волшебная верстка
  hasItems: boolean;
  isActive: boolean;
  subtitle?: string;
  isConfirmed: boolean;
  categoryId?: number;
  hasFrequency: boolean;
  categoryName: string;
  categoryType?: ECategoryType; // У системных подписок нет типа
}

export const categoriesReducer: Reducer<IGroup[], TCategoriesAction> = (state: IGroup[], action): IGroup[] => {
  switch (action.type) {
    case 'IIsActiveCategoryUpdated':
      return updateIsActiveGroups(state, action);
    default:
      return state || null;
  }
};

function updateIsActiveGroups(state: IGroup[], params: TCategoriesAction): IGroup[] {
  return state.map(group => ({
    ...group,
    categories: group.categories.map(category => {
      if (params.categoryId) {
        return category.categoryId === params.categoryId ? { ...category, isActive: params.isActive } : category;
      } else {
        return category.categoryName === params.categoryName ? { ...category, isActive: params.isActive } : category;
      }
    }),
  }));
}
